import "./style.css";
import {
  Button,
  notification,
  Table,
  Input,
  Space,
  Tag,
} from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";

import { useHistory, useLocation } from "react-router-dom";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { useDispatch, useSelector } from "react-redux";
import { actions, reducer } from "./slice";
import { reviewReportsSaga } from "./saga";
import * as selectors from "./selectors";
import { useEffect, useState } from "react";
import {
  endDateFormatter,
  startDateFormatter,
} from "../../../../utils/formatter";
import ReviewReportsAction from "./components/ReviewReportsAction";
import { useAuth } from "../../../../contexts/auth";
import Filters from "./Filters";
import Text from "antd/lib/typography/Text";

const sliceKey = "reviewReports";
const STORAGE_KEY = 'reviewReportsFilters';

const ReviewReports = () => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: reviewReportsSaga });

  const dispatch = useDispatch();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const history = useHistory();
  const location = useLocation();
  
  const companyId = user?.company?._id;
  const userRole = user?.role;
  const labId = user?.labDetails?._id;

  const loading = useSelector(selectors.selectLoading);
  const reports = useSelector(selectors.selectReports);
  const error = useSelector(selectors.selectError);
  const totalReports = useSelector(selectors.selectTotalReports);
  const commodities = useSelector(selectors.selectCommodities) || [];

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedState = JSON.parse(saved);
        // Validate that saved state matches current user context
        if (parsedState.userRole === userRole && parsedState.userId === user?._id) {
          return parsedState;
        }
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    
    return {
      filters: {},
      currentPage: 1,
      pageSize: 10,
      selectedClient: companyId || null,
      selectedProject: null,
      selectedLab: null,
      userRole: userRole,
      userId: user?._id,
    };
  };

  const initialState = getInitialState();
  const [filters, setFilters] = useState(initialState.filters);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [selectedClient, setSelectedClient] = useState(initialState.selectedClient);
  const [selectedProject, setSelectedProject] = useState(initialState.selectedProject);
  const [selectedLab, setSelectedLab] = useState(initialState.selectedLab);

  useEffect(() => {
    const state = {
      filters,
      currentPage,
      pageSize,
      selectedClient,
      selectedProject,
      selectedLab,
      userRole: userRole,
      userId: user?._id,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [filters, currentPage, pageSize, selectedClient, selectedProject, selectedLab, userRole, user]);

useEffect(() => {
  if (selectedLab && selectedClient && selectedProject) {
    let query = `page=${currentPage}&limit=${pageSize}&labId=${selectedLab}&companyId=${selectedClient}&projectCodeId=${selectedProject}`;
    
    // Apply saved filters to query
    if (filters.orderId) {
      query += `&orderId=${filters.orderId[0]}`;
    }
    if (filters.sampleSealNo) {
      query += `&sampleSealNo=${filters.sampleSealNo[0]}`;
    }
    if (filters.qrCodeId) {
      query += `&qrCodeId=${filters.qrCodeId[0]}`;
    }
    if (filters.parsingReviewStatus) {
      filters.parsingReviewStatus.forEach((cat) => {
        query += `&reviewStatus=${cat}`;
      });
    }
    if (filters.commodity) {
      filters.commodity.forEach((cat) => {
        query += `&commodityId=${cat}`;
      });
    }
    if (filters.date?.length === 2) {
      let startDate = startDateFormatter(filters.date[0]);
      let endDate = endDateFormatter(filters.date[1]);
      query += `&createdAtFrom=${startDate}&createdAtTo=${endDate}`;
    }
    dispatch(actions.fetchReports({ query }));
  } else if (userRole === "lab_admin" && labId) {
    let query = `page=${currentPage}&limit=${pageSize}&labId=${labId}`;
    
    // Apply same filters for lab_admin
    if (filters.orderId) {
      query += `&orderId=${filters.orderId[0]}`;
    }
    if (filters.sampleSealNo) {
      query += `&sampleSealNo=${filters.sampleSealNo[0]}`;
    }
    if (filters.qrCodeId) {
      query += `&qrCodeId=${filters.qrCodeId[0]}`;
    }
    if (filters.parsingReviewStatus) {
      filters.parsingReviewStatus.forEach((cat) => {
        query += `&reviewStatus=${cat}`;
      });
    }
    if (filters.commodity) {
      filters.commodity.forEach((cat) => {
        query += `&commodityId=${cat}`;
      });
    }
    if (filters.date?.length === 2) {
      let startDate = startDateFormatter(filters.date[0]);
      let endDate = endDateFormatter(filters.date[1]);
      query += `&createdAtFrom=${startDate}&createdAtTo=${endDate}`;
    }
    dispatch(actions.fetchReports({ query }));
  }
}, [dispatch, selectedClient, selectedProject, selectedLab, currentPage, pageSize, filters, userRole, labId]);
  useEffect(() => {
    if ((selectedLab && selectedClient && selectedProject) || (userRole === "lab_admin" && labId)) {
      dispatch(actions.fetchCommodities());
    }
  }, [dispatch, selectedLab, selectedClient, selectedProject, userRole, labId]);

  useEffect(() => {
    if (error) {
      notification.error({
        message: error,
      });
    }
  }, [error]);

  const handleClientChange = (value) => {
    setSelectedClient(value);
    setSelectedProject(null);
    setCurrentPage(1);
    setFilters({});
    const params = new URLSearchParams();
    if (value) params.set("client", value);
    if (selectedLab) params.set("lab", selectedLab);
    params.set("page", "1");
    params.set("limit", pageSize.toString());
  };

  const handleProjectChange = (value) => {
    setSelectedProject(value);
    setCurrentPage(1);
    setFilters({});
    const params = new URLSearchParams();
    if (selectedClient) params.set("client", selectedClient);
    if (selectedLab) params.set("lab", selectedLab);
    if (value) params.set("project", value);
    params.set("page", "1");
    params.set("limit", pageSize.toString());
  };

  const handleLabChange = (value) => {
    setSelectedLab(value);
    setSelectedProject(null);
    setCurrentPage(1);
    setFilters({});
    const params = new URLSearchParams();
    if (selectedClient) params.set("client", selectedClient);
    if (value) params.set("lab", value);
    params.set("page", "1");
    params.set("limit", pageSize.toString());
  };

  const clearAllFilters = () => {
    setSelectedClient(companyId || null);
    setSelectedProject(null);
    setSelectedLab(null);
    setCurrentPage(1);
    setFilters({});
    
    // Clear sessionStorage
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear filters:', e);
    }
    };

  const reportsData = reports?.map((report) => ({
    orderId: report?.orderId,
    commodity: report?.commodity,
    parsingReviewStatus: report?.parsingReviewStatus,
    qrCodeId:
      report?.sampleDetails?.qrCodeId &&
      report?.sampleDetails?.qrCodeId.length > 25
        ? `${report?.sampleDetails?.qrCodeId?.slice(0, 25)}...`
        : report?.sampleDetails?.qrCodeId,
    sampleSealNo: report?.sampleDetails?.sampleSealNo,
    id: report._id,
    totalParameters:
      report?.reviewResult?.parametersOverview?.totalParameters ?? "-",
    matchedParameters:
      report?.reviewResult?.parametersOverview?.matchedParameters ?? "-",
    missingParameters:
      report?.reviewResult?.parametersOverview?.missingParameters ?? "-",
    manuallyMatchedParameters:
      report?.reviewResult?.parametersOverview?.manuallyMatchedParameters ??
      "-",
    notTestedParameters:
      report?.reviewResult?.parametersOverview?.notTestedParameters ?? "-",
    overallCompliance:
      report?.reviewResult?.sampleComplianceDetails
        ?.overallSampleClassification ?? "-",
  }));

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const getReviewStatusFilters = () => {
    return [
      "Processing",
      "ReviewPending",
      "ReviewInProgress",
      "ProcessingFailed",
      "Under Dispute",
      "ReviewUnderverification",
      "ReviewCompleted",
    ].sort((a, b) => a.localeCompare(b)).map((key) => ({
      text: key,
      value: key,
    }));
  };

  const columns = [
    {
      title: "ORDER ID",
      dataIndex: "orderId",
      fixed: "left",
      width: "15%",
      ...getColumnSearchProps("orderId"),
      filteredValue: filters.orderId || null,
    },
    {
      title: "Seal Id",
      dataIndex: "sampleSealNo",
      width: "7%",
      ...getColumnSearchProps("sampleSealNo"),
      filteredValue: filters.sampleSealNo || null,
    },
    {
      title: "QR Code Id",
      dataIndex: "qrCodeId",
      width: "7%",
      ...getColumnSearchProps("qrCodeId"),
      filteredValue: filters.qrCodeId || null,
    },
    {
      title: "Commodity",
      dataIndex: "commodity",
      width: "12%",
      filterSearch: true,
      filters: commodities.slice().sort((a, b) => a.name.localeCompare(b.name)).map((commodity) => ({
        text: commodity.name,
        value: commodity._id,
      })),
      filteredValue: filters.commodity || null,
    },
    {
      title: "Status",
      dataIndex: "parsingReviewStatus",
      width: "10%",
      filterSearch: true,
      filters: getReviewStatusFilters(),
      filteredValue: filters.parsingReviewStatus || null,
      render: (status) => (
        <Tag
          color={statusColorMap[status] || "default"}
          style={{
            fontWeight: 500,
            fontSize: 12,
            padding: "2px 8px",
            borderRadius: "6px",
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Total Parameters",
      dataIndex: "totalParameters",
      width: "6%",
    },
    {
      title: "Matched Parameters",
      dataIndex: "matchedParameters",
      width: "6%",
    },
    {
      title: "Missing Parameters",
      dataIndex: "missingParameters",
      width: "6%",
    },
    {
      title: "Manually Matched Parameters",
      dataIndex: "manuallyMatchedParameters",
      width: "8%",
    },
    {
      title: "Not Tested Parameters",
      dataIndex: "notTestedParameters",
      width: "6%",
    },
    {
      title: "Overall Compliance",
      dataIndex: "overallCompliance",
      width: "6%",
      render: (overallCompliance) => (
        <Tag
          color={overallComplianceColorMap[overallCompliance] || "default"}
          style={{
            fontWeight: 500,
            fontSize: 12,
            padding: "2px 8px",
            borderRadius: "6px",
          }}
        >
          {overallCompliance}
        </Tag>
      ),
    },
    {
      title: "Action",
      width: "5%",
      fixed: "right",
      render: (_, record) => (
        <ReviewReportsAction
          record={record}
          userRole={userRole}
          fetchReports={() => {
            // Trigger refetch by updating a dependency
            if (selectedLab && selectedClient && selectedProject) {
              let query = `page=${currentPage}&limit=${pageSize}&labId=${selectedLab}&companyId=${selectedClient}&projectCodeId=${selectedProject}`;
              dispatch(actions.fetchReports({ query }));
            } else if (userRole === "lab_admin" && labId) {
              let query = `page=${currentPage}&limit=${pageSize}&labId=${labId}`;
              dispatch(actions.fetchReports({ query }));
            }
          }}
        />
      ),
    },
  ];

  const handleTablePagination = (pagination, tableFilters, sorter) => {
    const newPage = pagination.current;
    const newPageSize = pagination.pageSize;
    
    // Update state
    setCurrentPage(newPage);
    setPageSize(newPageSize);
    setFilters({
      orderId: tableFilters.orderId || null,
      sampleSealNo: tableFilters.sampleSealNo || null,
      qrCodeId: tableFilters.qrCodeId || null,
      commodity: tableFilters.commodity || null,
      parsingReviewStatus: tableFilters.parsingReviewStatus || null,
      status: tableFilters.status || null,
      date: tableFilters.date || null,
    });
    
    let query = `page=${newPage}&limit=${newPageSize}`;

    if (selectedLab && selectedClient && selectedProject) {
      query += `&labId=${selectedLab}&companyId=${selectedClient}&projectCodeId=${selectedProject}`;
    } else if (userRole === "lab_admin" && labId) {
      query += `&labId=${labId}`;
    }

    if (tableFilters.status) {
      tableFilters.status.forEach((cat) => {
        query += `&status=${cat}`;
      });
    }

    if (tableFilters.orderId) {
      query += `&orderId=${tableFilters.orderId[0]}`;
    }

    if (tableFilters.sampleSealNo) {
      query += `&sampleSealNo=${tableFilters.sampleSealNo[0]}`;
    }

    if (tableFilters.qrCodeId) {
      query += `&qrCodeId=${tableFilters.qrCodeId[0]}`;
    }

    if (tableFilters.parsingReviewStatus) {
      tableFilters.parsingReviewStatus.forEach((cat) => {
        query += `&reviewStatus=${cat}`;
      });
    }

    if (tableFilters.commodity) {
      tableFilters.commodity.forEach((cat) => {
        query += `&commodityId=${cat}`;
      });
    }

    if (tableFilters.date?.length === 2) {
      let startDate = startDateFormatter(tableFilters.date[0]);
      let endDate = endDateFormatter(tableFilters.date[1]);
      query += `&createdAtFrom=${startDate}&createdAtTo=${endDate}`;
    }

    dispatch(actions.fetchReports({ query }));
    const params = new URLSearchParams();
    if (selectedClient) params.set("client", selectedClient);
    if (selectedLab) params.set("lab", selectedLab);
    if (selectedProject) params.set("project", selectedProject);
    if (newPage !== 1) params.set("page", newPage.toString());
    if (newPageSize !== 10) params.set("limit", newPageSize.toString());
  };

  return (
    <>
      <div
        className="labReviewReportsHeader"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <p style={{ fontSize: "18px", fontWeight: "bold", margin: 0 }}>
          Review Reports
        </p>

        {userRole !== "lab_admin" && (
          <Button
            onClick={clearAllFilters}
            icon={<ReloadOutlined />}
            style={{ marginLeft: 8 }}
          >
            Reset filters
          </Button>
        )}
      </div>

      {userRole === "lab_admin" ? null : (
        <Filters
          userRole={userRole}
          selectedClient={selectedClient}
          selectedProject={selectedProject}
          selectedLab={selectedLab}
          handleClientChange={handleClientChange}
          handleProjectChange={handleProjectChange}
          handleLabChange={handleLabChange}
        />
      )}
      <div>
        {userRole === "lab_admin" ? (
          <Table
            columns={columns}
            dataSource={reportsData}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1800 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalReports,
              showSizeChanger: true,
              disabled: !reports?.length,
              showTotal: (total, range) => (
                <Text>{`${range[0]}-${range[1]} of ${total} items`}</Text>
              ),
            }}
            onChange={handleTablePagination}
            rowClassName={(record) =>
              record.parsingReviewStatus === "Under Dispute"
                ? "under-dispute-row"
                : ""
            }
          />
        ) : selectedClient && selectedLab && selectedProject ? (
          <Table
            columns={columns}
            dataSource={reportsData}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1800 }}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalReports,
              showSizeChanger: true,
              disabled: !reports?.length,
              showTotal: (total, range) => (
                <Text>{`${range[0]}-${range[1]} of ${total} items`}</Text>
              ),
            }}
            onChange={handleTablePagination}
            rowClassName={(record) =>
              record.parsingReviewStatus === "Under Dispute"
                ? "under-dispute-row"
                : ""
            }
          />
        ) : (
          <div
            style={{
              textAlign: "center",
              marginTop: "20px",
              background: "#f5f5f5",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <p style={{ fontSize: "16px", fontWeight: "bold" }}>
              Kindly select all filters to view the orders.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ReviewReports;

const statusColorMap = {
  ProcessingFailed: "red",
  Processing: "cyan",
  ReviewInProgress: "orange",
  ReviewPending: "blue",
  ReviewUnderverification: "purple",
  ReviewCompleted: "green",
  "Under Dispute": "red",
  default: "gray",
};

const overallComplianceColorMap = {
  Compliant: "green",
  Safe: "green",
  "Sub-Standard": "orange",
  Unsafe: "red",
  "Mis-Labelled": "gray",
  default: "gray",
};